import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import Button from "../../components/common/button";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";
import ProductData from "../../components/productData";
import SearchItem from "../../components/searchItem";
import { useViewProductQuery } from "../../hooks/products/viewAllProducts";
import { useRemoveProduct } from "../../hooks/products/deleteProduct";
import { toast } from "react-hot-toast";
import ProductModal from "../../components/modal/productModal";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import { useViewSubscription } from "../../hooks/subscription/subscription.view";
import {
  getPlanCreationLimits,
  getPlanLimitMessage,
} from "../subscription/planLimits";

const Products = () => {
  const [localProducts, setLocalProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [mode, setMode] = useState("create");
  const [modalKey, setModalKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const {
    data: productsResponse,
    isLoading,
    isFetching,
    isError,
  } = useViewProductQuery({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutate: removeProduct, isPending: isDeleting } = useRemoveProduct();
  const { data: subscriptionResponse, isPlaceholderData } = useViewSubscription();
  const planLimits = getPlanCreationLimits(subscriptionResponse, {
    isReady: !isPlaceholderData,
  });

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!productsResponse) return;

    setLocalProducts(productsResponse.products ?? []);

    const apiPagination = productsResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [productsResponse, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleDelete = (product) => {
    if (!product) return;
    setEditProduct(product);
    setMode("delete");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  if (isLoading) return <div>Loading products...</div>;
  if (isError) {
    return <div>Error loading products</div>;
  }

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            Manage your product catalog
          </p>
        </div>
        <div className="page-body-actions">
          <Button
            disabled={!planLimits.products.canCreate}
            onClick={() => {
              if (!planLimits.products.canCreate) {
                toast.error(getPlanLimitMessage("products", planLimits));
                return;
              }
              navigate("/products/addproducts");
            }}
          >
            + Add Product
          </Button>
          <p
            className={`plan-limit-hint${planLimits.products.canCreate ? "" : " is-limit"}`}
          >
            {getPlanLimitMessage("products", planLimits)}
          </p>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localProducts}
            searchField={["name", "title"]}
            placeholder="Search by product name..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => {
              setSearchTerm(searchItem);
            }}
          />

          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <ProductData products={localProducts} onDelete={handleDelete} />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />

      <ProductModal
        key={modalKey}
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setEditProduct(null);
          setMode("create");
        }}
        editProduct={editProduct}
        mode={mode}
        onSubmit={() => {}}
        isLoading={false}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setEditProduct(null);
          setMode("create");
        }}
        onConfirm={() => {
          const id = editProduct?._id || editProduct?.id;
          removeProduct(id, {
            onSuccess: () => {
              toast.success("Product deleted successfully!");
              setIsModalOpen(false);
              setEditProduct(null);
            },
            onError: () => {
              toast.error("Failed to delete product. Please try again.");
            },
          });
        }}
        title="Delete Product"
        itemName={editProduct?.name || editProduct?.title}
        isLoading={isDeleting}
      />
    </>
  );
};

export default Products;

